// Os rótulos das superfícies de viagem, num lugar só. Três telas escrevem as
// MESMAS frases sobre os mesmos dados — o card da home, o painel da viagem
// (itinerário compacto) e o card do voucher (detalhe completo) —, e frase
// duplicada divergiria no primeiro ajuste de cópia. Puro: `ctx.t()` + os
// helpers deste pacote, sem estado.
import type {
  BaggageAllowance,
  BaggageAllowanceLine,
  RentalAgency,
  VoucherView,
} from '@voll-intelligence/domain';
import type { DocumentContext } from './context.js';
import { calendarDateFromUtcDay, monthShort } from './dates.js';
import { splitAirport } from './format.js';
import type { ServiceLeg, TripService, TripServiceDetail, TripStop } from './services.js';
import type { ModalKind } from './status.js';

/** O nome do modal na palavra de MERCADO ("Transfer", "Carro") — a mesma que os tiles de
 *  acrescentar serviço e o calendário já usam. Não confundir com `modalLabel`, do
 *  `voucher_meta`, que fala o vocabulário da HOME ("Mobilidade", "Carros"): são duas
 *  famílias de cópia vivas, e a desta é a das superfícies de viagem. */
export const MODAL_LABEL_KEY: Record<ModalKind, string> = {
  FLIGHT: 'trips.calendar.modal.flight',
  HOTEL: 'trips.calendar.modal.hotel',
  BUS: 'trips.calendar.modal.bus',
  RENTAL: 'trips.calendar.modal.rental',
  MOBILITY: 'trips.calendar.modal.mobility',
};

/** "Localiza · Av. X, 100 · Recife" — nome, endereço e cidade da agência de
 *  locação, cada parte cai sozinha quando a fonte não a tem. */
export function agencyLine(agency: RentalAgency | null): string | null {
  if (!agency) return null;
  return [agency.name, agency.address, agency.city].filter(Boolean).join(' · ') || null;
}

/** `fares.baggage` da fonte é a FRANQUIA EM QUILOS, e ela chega como número pelado.
 *
 *  Quem diz isso é o produtor: o `voucher-api` monta a prosa dele com
 *  ``luggageRule: `Até ${fares.baggage} Kg` `` (`src/maps/flight/get.data.flight.js`). O campo
 *  não carrega unidade nenhuma, e o painel o imprimia verbatim — daí o "BAGAGEM 10" pelado na
 *  tela, que não diz nem que são quilos.
 *
 *  A unidade é opcional na fonte: `'10'` e `'10 Kg'` ambos aparecem nos dados reais. Este
 *  grupo captura o número dos dois jeitos, e prosa de verdade ("1 peça de 23kg") não casa e
 *  passa intacta — a fonte às vezes sabe mais do que um número. */
const BAGGAGE_KG = /^(\d+(?:[.,]\d+)?)\s*(?:kgs?|quilos?)?$/i;

/** O que a fonte manda em `baggage` que NÃO é bagagem, e que ia parar na tela.
 *
 *  `'undefined'` é literal — as nove letras, não o valor: o produtor interpola um campo
 *  ausente numa string e o resultado atravessa a cadeia inteira como texto válido (está nos
 *  fakers dele). Zero é o outro caso, e é diferente de ausente: `'0'` como franquia não
 *  afirma "sem bagagem", só não informa quantidade — quem afirma é o `checkedBaggage`, e é a
 *  ele que os dois casos deferem. */
const BAGGAGE_JUNK = new Set(['', 'undefined', 'null', 'nan']);

/** O trecho, pelos três campos que falam de bagagem: os dois antigos e a franquia que a
 *  tarifa declara. */
interface BaggageLeg {
  baggage?: string | null;
  checkedBaggage?: boolean | null;
  baggageAllowance?: BaggageAllowance | null;
}

/** A franquia DECLARADA pela tarifa, quando ela diz quanto. `null` quando a linha não traz
 *  número nenhum — e aí quem fala é o booleano, como sempre falou; inventar peça ou quilo
 *  para preencher a linha seria afirmar o que a companhia não disse. */
function checkedAllowanceLabel(
  line: BaggageAllowanceLine | null | undefined,
  ctx: DocumentContext,
): string | null {
  if (line == null) return null;
  if (line.included === false) return ctx.t('trips.services.baggageNone');
  const { pieces, weightKg: kg } = line;
  if (pieces !== null && kg !== null) {
    return ctx.t('trips.services.baggagePiecesKg', { count: pieces, kg });
  }
  if (pieces !== null) return ctx.t('trips.services.baggagePieces', { count: pieces });
  if (kg !== null) return ctx.t('trips.services.baggageCheckedKg', { kg });
  return line.included === true ? ctx.t('trips.services.baggageIncluded') : null;
}

/** A bagagem DESPACHADA do trecho: a franquia declarada pela tarifa (peças/peso), senão a
 *  franquia da fonte com a unidade que ela esquece, a prosa dela quando é prosa, senão a
 *  frase que o booleano merece. `null` quando ninguém sabe — e calar é diferente de "sem
 *  bagagem", que é o que `checkedBaggage: false` afirma. */
export function baggageLabel(leg: BaggageLeg, ctx: DocumentContext): string | null {
  const declared = checkedAllowanceLabel(leg.baggageAllowance?.checked, ctx);
  if (declared !== null) return declared;
  const raw = leg.baggage?.trim() ?? '';
  const kg = BAGGAGE_KG.exec(raw)?.[1];
  // A vírgula vira ponto ANTES da conta, e não é preciosismo: o grupo aceita `0,5`, e
  // `parseFloat` para na vírgula e devolve `0` — a franquia cairia no ramo do zero e sumiria
  // da tela. O texto exibido segue sendo o `kg` original, com a vírgula que a fonte mandou.
  const vazio =
    BAGGAGE_JUNK.has(raw.toLowerCase()) || (kg !== undefined && Number(kg.replace(',', '.')) === 0);
  if (!vazio) {
    if (kg === undefined) return raw;
    // Número + despacho confirmado: a fonte mistura mão e despacho no mesmo campo (tarifa
    // com 1×23kg despachada chega com `baggage: '10'`, os quilos da bagagem de MÃO), então
    // o número não é confiável como franquia — a frase do booleano é. Prosa (acima) segue
    // vencendo porque é mais específica que o booleano.
    if (leg.checkedBaggage === true) return ctx.t('trips.services.baggageIncluded');
    return ctx.t('trips.services.baggageKg', { kg });
  }
  if (leg.checkedBaggage == null) return null;
  return ctx.t(
    leg.checkedBaggage ? 'trips.services.baggageIncluded' : 'trips.services.baggageNone',
  );
}

/** A bagagem de MÃO, que só a franquia declarada conhece — `fares.baggage` mistura as duas e
 *  `checkedBaggage` só fala do despacho, então esta linha nasce vazia e some da tela sempre
 *  que a tarifa não a declarou. Vive à parte de `baggageLabel` porque são duas franquias
 *  distintas: juntá-las numa frase é o que escondia a do despacho. */
function carryBaggageLabel(leg: BaggageLeg, ctx: DocumentContext): string | null {
  const line = leg.baggageAllowance?.carry;
  if (line == null) return null;
  if (line.included === false) return ctx.t('trips.services.baggageCarryNone');
  const { pieces, weightKg: kg } = line;
  if (pieces !== null && kg !== null) {
    return ctx.t('trips.services.baggageCarryPiecesKg', { count: pieces, kg });
  }
  if (pieces !== null) return ctx.t('trips.services.baggageCarryPieces', { count: pieces });
  if (kg !== null) return ctx.t('trips.services.baggageKg', { kg });
  return line.included === true ? ctx.t('trips.services.baggageCarryIncluded') : null;
}

/** As DUAS linhas de bagagem do bilhete, rotuladas e na ordem da tela: despachada e, quando a
 *  tarifa a declarou, a de mão. Ficam juntas porque são a MESMA decisão de cópia — foi
 *  espremer as duas franquias numa linha só que apagou a do despacho (issue #3305). A linha
 *  sem valor morre em quem desenha, pela regra de caractere de cada superfície. */
export function baggageFields(
  leg: BaggageLeg,
  ctx: DocumentContext,
): { key: string; label: string; value: string | null }[] {
  return [
    { key: 'baggage', label: ctx.t('trips.services.baggage'), value: baggageLabel(leg, ctx) },
    {
      key: 'carryBaggage',
      label: ctx.t('trips.services.carryBaggage'),
      value: carryBaggageLabel(leg, ctx),
    },
  ];
}

/** "direto" / "1 parada"; null quando a fonte não informa paradas. Recebe o número
 *  cru, e não o trecho, porque as superfícies guardam esse dado em formas
 *  diferentes (o trecho do voucher, o voo em destaque) e a frase é a mesma. */
export function stopsLabel(stops: number | null | undefined, ctx: DocumentContext): string | null {
  if (stops == null) return null;
  return stops === 0
    ? ctx.t('trips.featured.direct')
    : ctx.t('trips.featured.stops', { count: stops });
}

/** "Ida" / "Volta"; null no ônibus comprado online (a fonte não marca a perna). */
export function legLabel(leg: Pick<ServiceLeg, 'leg'>, ctx: DocumentContext): string | null {
  if (leg.leg === null) return null;
  return ctx.t(leg.leg === 'volta' ? 'trips.services.legBack' : 'trips.services.legOut');
}

/** "GOL 1769" — quem opera o trecho. Companhia e número são UM dado (o designador
 *  do voo), e por isso se separam por espaço e não por "·"; no ônibus a fonte não
 *  numera o trecho e sobra a empresa. */
export function carrierLabel(leg: Pick<ServiceLeg, 'carrier' | 'number'>): string | null {
  // A fonte manda companhia com espaço sobrando ("LATAM Airlines ") e o join
  // dobrava o espaço na linha do itinerário.
  return [leg.carrier?.trim(), leg.number?.trim()].filter(Boolean).join(' ') || null;
}

/** A rota do trecho ("CNF → GIG"); null quando a fonte não deu as duas pontas —
 *  meia flecha diz menos que nada. */
export function routeLabel(leg: Pick<ServiceLeg, 'from' | 'to'>): string | null {
  const from = splitAirport(leg.from).code;
  const to = splitAirport(leg.to).code;
  return from && to ? `${from} → ${to}` : null;
}

/** As quatro cabines que reconhecemos, por como a fonte as escreve (sem acento,
 *  maiúsculas, só letras). Lista FECHADA de propósito: `fares.class` é na maior
 *  parte dos vouchers a letra da classe de reserva ("O", "L") e às vezes a marca
 *  tarifária ("LIGHT", "PROMO") — nada disso é cabine, e um filtro por tamanho
 *  ("mais de 2 caracteres") deixaria a marca passar por cabine. */
const CABIN_KEYS: Record<string, string> = {
  ECONOMICA: 'trips.cabin.economy',
  ECONOMY: 'trips.cabin.economy',
  PREMIUM: 'trips.cabin.premium',
  PREMIUMECONOMY: 'trips.cabin.premium',
  EXECUTIVA: 'trips.cabin.business',
  EJECUTIVA: 'trips.cabin.business',
  BUSINESS: 'trips.cabin.business',
  PRIMEIRA: 'trips.cabin.first',
  PRIMERA: 'trips.cabin.first',
  FIRST: 'trips.cabin.first',
};

/** A cabine, quando o que veio É cabine — e traduzida: valor cru da fonte dentro
 *  de uma frase montada por `t()` não passa por i18n nenhum.
 *
 *  Exportada porque o itinerário PROVISÓRIO (`provisional.ts`) aplica o mesmo guard
 *  sobre o `fareClass` do recibo. A régua é a LISTA FECHADA, não o tamanho da
 *  string — duas implementações divergiriam na primeira marca tarifária nova. */
export function cabinLabel(travelClass: string | null, ctx: DocumentContext): string | null {
  const key = travelClass
    ?.normalize('NFD')
    .replaceAll(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replaceAll(/[^A-Z]/g, '');
  const messageKey = key ? CABIN_KEYS[key] : undefined;
  return messageKey ? ctx.t(messageKey) : null;
}

// ── Rótulos de SERVIÇO e de PASSO do itinerário ──────────────────────────────
// Lifted de `apps/clients/web-chat/src/ui/trips/labels.ts` na Task 6, junto com
// `services.ts` — `stopTitle`/`stopDetail`/`serviceHeading`/`serviceSubtitle`
// leem `TripStop`/`TripService`, que só existem a partir desta task.

/** O nome do modal na palavra da HOME ("Aéreo", "Carros") — vocabulário
 *  DIFERENTE do `MODAL_LABEL_KEY` acima (a família do MERCADO, "Voos"/"Carro",
 *  usada pelos tiles e pelo calendário) e do `trips.voucher.modal.*` do
 *  documento do voucher: três famílias de cópia vivas, cada uma com seu
 *  chamador. Verbatim de `voucher_meta.ts` do web-chat — este pacote não o
 *  importa (é dependência exclusiva do web-chat), então a lógica é reproduzida
 *  aqui, com `ctx.t()` no lugar do `t()` do i18next. */
function modalLabel(modal: string, ctx: DocumentContext): string {
  switch (modal) {
    case 'FLIGHT':
      return ctx.t('home.service.flight.label');
    case 'HOTEL':
      return ctx.t('home.service.hotel.label');
    case 'BUS':
      return ctx.t('home.service.bus.label');
    case 'RENTAL':
      return ctx.t('home.service.rental.label');
    case 'MOBILITY':
      return ctx.t('home.service.mobility.label');
    default:
      return modal;
  }
}

/** Fornecedor, senão o rótulo do modal, senão o texto de fallback do chamador.
 *  Aceita o subconjunto de campos que a regra usa — a linha do tempo da viagem
 *  chama com o serviço derivado, não com o `VoucherView` cru. Verbatim de
 *  `voucher_meta.ts`, pela mesma razão que `modalLabel` acima. */
function voucherTitle(
  voucher: Pick<VoucherView, 'supplier' | 'modal'>,
  fallback: string,
  ctx: DocumentContext,
): string {
  if (voucher.supplier) return voucher.supplier;
  if (voucher.modal) return modalLabel(voucher.modal, ctx);
  return fallback;
}

type HotelDetail = Extract<TripServiceDetail, { kind: 'HOTEL' }>;

/** "15 Jul" de uma data-só armazenada à meia-noite UTC.
 *
 *  Renomeado do `dayLabel` original de `ui/trips/labels.ts` do web-chat: o
 *  pacote já exporta um `dayLabel(date, locale)` DIFERENTE (o cabeçalho do dia,
 *  COM dia da semana — "Ter, 25 Ago" — lifted de `voucher_document_view.ts` na
 *  Task 4). Os dois `dayLabel`s do web-chat nunca colidem porque vivem em
 *  módulos separados; aqui os dois entram no MESMO barrel (`export *` em
 *  `index.ts`), e manter o nome duplicado quebraria a compilação do pacote. */
function hotelDateLabel(date: Date | null, ctx: DocumentContext): string | null {
  const local = calendarDateFromUtcDay(date);
  return local ? `${String(local.getDate())} ${monthShort(local, ctx.locale)}` : null;
}

/** "check-in 17 Jun → check-out 20 Jun · 3 noites". */
function hotelWindowLabel(detail: HotelDetail, ctx: DocumentContext): string | null {
  const checkIn = hotelDateLabel(detail.checkIn, ctx);
  const checkOut = hotelDateLabel(detail.checkOut, ctx);
  const window =
    checkIn && checkOut
      ? `${ctx.t('trips.agenda.checkIn')} ${checkIn} → ${ctx.t('trips.agenda.checkOut')} ${checkOut}`
      : null;
  const nights =
    detail.nights == null ? null : ctx.t('trips.agenda.nights', { count: detail.nights });
  return [window, nights].filter(Boolean).join(' · ') || null;
}

/** "check-out 20 Jun · 3 noites" — a estada no ITINERÁRIO, onde a entrada já foi
 *  dita duas vezes: a data do check-in é o cabeçalho do dia e a hora é a calha do
 *  passo. O que o detalhe acrescenta é quando a estada termina. A janela completa
 *  (`hotelWindowLabel`) segue no card do voucher, que não tem esse contexto. */
export function hotelStayLabel(detail: HotelDetail, ctx: DocumentContext): string | null {
  const checkOut = hotelDateLabel(detail.checkOut, ctx);
  const nights =
    detail.nights == null ? null : ctx.t('trips.agenda.nights', { count: detail.nights });
  return (
    [checkOut && `${ctx.t('trips.agenda.checkOut')} ${checkOut}`, nights]
      .filter(Boolean)
      .join(' · ') || null
  );
}

/** Quem identifica o serviço: o hotel pelo NOME e o carro pelo MODELO — é o que a pessoa
 *  reconhece, e não o balcão que entrega ("Localiza" é onde se retira, "Fiat Cronos ou
 *  similar" é o que se dirige; o fornecedor segue no rodapé de quem tem rodapé). Os demais
 *  caem na regra compartilhada (`voucherTitle`: fornecedor, senão o rótulo do modal). */
export function serviceHeading(service: TripService, ctx: DocumentContext): string {
  const detail = service.detail;
  const own = detail?.kind === 'HOTEL' ? detail.name : detail?.kind === 'RENTAL' && detail.carModel;
  return own || voucherTitle(service, modalLabel(service.modal, ctx), ctx);
}

/** Subtítulo do serviço: a janela do hotel, o trajeto da corrida, a janela da
 *  locação. Em voo/ônibus fica vazio: os horários vivem nos trechos. */
export function serviceSubtitle(service: TripService, ctx: DocumentContext): string | null {
  const detail = service.detail;
  if (detail?.kind === 'FLIGHT' || detail?.kind === 'BUS') return null;
  if (detail?.kind === 'HOTEL') return hotelWindowLabel(detail, ctx);
  if (detail?.kind === 'MOBILITY') return detail.route;
  if (detail?.kind === 'RENTAL') {
    const pickup = detail.pickupTime
      ? `${ctx.t('trips.services.pickup')} ${detail.pickupTime}`
      : null;
    const dropoff = detail.dropoffTime
      ? `${ctx.t('trips.services.dropoff')} ${detail.dropoffTime}`
      : null;
    return [pickup, dropoff].filter(Boolean).join(' → ') || null;
  }
  return service.time || null;
}

/** O que nomeia UM passo do itinerário: a PERNA ("Voo de ida"), como no protótipo;
 *  o resto pelo serviço (nome do hotel, fornecedor). O número do voo é devolvido
 *  à parte porque a logo da companhia entra ENTRE os dois no render.
 *
 *  Ressalva conhecida, e o motivo de a rota estar no detalhe logo abaixo: a fonte
 *  tipa como `OUTWARD` cada voucher de perna única, então numa viagem de três
 *  trechos as três linhas dizem "Voo de ida" — o que as distingue é a linha de
 *  baixo. */
export function stopTitle(
  stop: TripStop,
  ctx: DocumentContext,
  /** O renderizador consegue MARCAR esta companhia com uma logo/marca visual? O
   *  pacote não sabe — quem desenha é que conhece o catálogo de marcas (3 SVGs
   *  curados no web-chat, nenhuma no papel). O host injeta esse conhecimento;
   *  o default `() => false` é "nenhum renderizador marca", que é exatamente
   *  certo para o PDF — sem marca no papel, o nome da companhia NUNCA pode
   *  sumir da linha. */
  hasCarrierMark: (carrier: string) => boolean = () => false,
): { name: string; flightNo: string | null } {
  const leg = stop.leg;
  if (!leg) return { name: serviceHeading(stop.service, ctx), flightNo: null };
  const flightLegLabel = ctx.t(
    leg.leg === 'volta' ? 'trips.agenda.flightBack' : 'trips.agenda.flightOut',
  );
  const name =
    stop.service.modal === 'FLIGHT'
      ? flightLegLabel
      : [modalLabel(stop.service.modal, ctx), legLabel(leg, ctx)].filter(Boolean).join(' · ');
  // Sem logo o nome da companhia entra junto do número, senão "1769" fica órfão:
  // a fonte manda o número sem o prefixo da cia. COM logo, o nome sai — mas só
  // porque a marca o substitui; quando a fonte não numera o trecho
  // (`flightNumbers: []`, que acontece em voucher não enriquecido) o nome volta,
  // senão a linha não diria quem opera o voo.
  const mark = leg.carrier != null && hasCarrierMark(leg.carrier);
  return { name, flightNo: (mark ? (leg.number ?? leg.carrier) : carrierLabel(leg)) ?? null };
}

/** A linha de detalhe do passo: rota + paradas + cabine no trecho; a estada nos
 *  modais sem trecho.
 *
 *  A CABINE só entra quando a fonte manda uma palavra ("ECONÔMICA"): o campo é o
 *  `fares.class`, que em boa parte dos vouchers é a letra da classe de reserva
 *  ("O", "L") — sozinha e sem rótulo ela passa por erro de renderização. Rotulada,
 *  ela vive no card do voucher. */
export function stopDetail(stop: TripStop, ctx: DocumentContext): string | null {
  const leg = stop.leg;
  if (leg) {
    return (
      [routeLabel(leg), stopsLabel(leg.stops, ctx), cabinLabel(leg.travelClass, ctx)]
        .filter(Boolean)
        .join(' · ') || null
    );
  }
  const detail = stop.service.detail;
  if (detail?.kind === 'HOTEL') return hotelStayLabel(detail, ctx);
  return serviceSubtitle(stop.service, ctx);
}
